import React, { useState } from "react";
import { useSiteConfig } from "../../context/SiteConfigContext";
import { Link } from "react-router-dom";
import contactAPI from "../../api/contactApi";
import { showSuccess, showError } from "../../utils/toastUtils";
import useAlphabetsInput from "../../hooks/useAlphabeticInput";
import useNumericInput from "../../hooks/useNumericInput";
import Breadcrump from "../../components/Breadcrump";
import { Container, Row, Col, Form } from "react-bootstrap";
import { Mail, Phone, MapPin, Instagram, Facebook } from "lucide-react";
import {
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaYoutube,
  FaLinkedin,
} from "react-icons/fa";

function ContactUs() {
  useAlphabetsInput();
  useNumericInput();
  const { config } = useSiteConfig();

  const INITAL_FORM_DATA = {
    email: "",
    phone: "",
    name: "",
    message: "",
  };

  const [formData, setFormData] = useState(INITAL_FORM_DATA);
  const [errors, setErrors] = useState({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(formData.email);

  const validate = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Please enter email address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone) {
      newErrors.phone = "Please enter phone";
    }

    if (!formData.name) {
      newErrors.name = "Please enter name";
    }

    if (!formData.message) {
      newErrors.message = "Please enter message";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const res = await contactAPI.store(formData);
      if (res.code === 200) {
        showSuccess(res.message);
        setFormData(INITAL_FORM_DATA);
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  return (
    <>
      <Breadcrump title="Contact Us" />

      <div className="contact-page-wrapper">
        {/* Hero Header */}
        <section className="contact-hero text-center py-5">
          <Container>
            <span className="subtitle">Get In Touch</span>
            <h1 className="section-title mt-2">We’d Love to Hear From You</h1>
            <p
              className="text-secondary mx-auto mt-2"
              style={{ maxWidth: "900px" }}
            >
              For any questions or queries, whether you're already a customer or
              a potential customer, feel free to reach out to our friendly and
              knowledgable customer support team.
            </p>
          </Container>
        </section>

        <Container className="pb-5">
          <div className="contact-card-container shadow-sm">
            <Row className="g-0">
              {/* Left Side: Info */}
              <Col lg={5} className="info-panel p-md-5 p-3 text-white">
                <h3 className="contact-title mb-3">Contact Information</h3>
                <div className="info-item d-flex align-items-start mb-3">
                  <div>
                    <h6>Support Timings</h6>
                    <a className="contact-info" href="#">
                      Monday - Sunday (11:00 am - 8:30 pm)
                    </a>
                  </div>
                </div>

                <div className="info-item d-flex align-items-start mb-3">
                  <div>
                    <h6>Address</h6>
                    <a href="#" className="contact-info">
                      {[
                        config?.address?.addressLine,
                        config?.address?.district,
                        config?.address?.state,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                      {config?.address?.pincode
                        ? ` - ${config?.address.pincode}`
                        : ""}
                    </a>
                  </div>
                </div>

                {config?.whatsappNumber && (
                  <div className="info-item d-flex align-items-start mb-3">
                    <div>
                      <h6>Whatsapp Here</h6>
                      <a
                        className="contact-info"
                        href={`https://wa.me/${config?.whatsappNumber}`}
                        target="_blank"
                      >
                        +91 {config?.whatsappNumber}
                      </a>
                    </div>
                  </div>
                )}

                <div className="info-item d-flex align-items-start mb-3">
                  <div>
                    <h6>Phone number</h6>
                    <a
                      className="contact-info"
                      href={`tel:+91 ${config?.phone}`}
                    >
                      +91 {config?.phone}
                    </a>
                  </div>
                </div>

                <div className="info-item d-flex align-items-start mb-3">
                  <div>
                    <h6>Email</h6>
                    <a
                      className="contact-info"
                      href={`mailto: ${config?.email}`}
                    >
                      {config?.email}
                    </a>
                  </div>
                </div>
                <p>
                  Please note that during public holidays, our response may take
                  longer than usual. We will always respond as soon as possible.
                </p>

                <div className="social-icons d-flex gap-3 pt-4 border-top border-light-subtle">
                  {config?.socialMedia?.facebook && (
                    <li>
                      <a
                        href={config?.socialMedia?.facebook}
                        target="_blank"
                        className="social-icon"
                      >
                        <FaFacebook size={16} />
                      </a>
                    </li>
                  )}
                  {config?.socialMedia?.instagram && (
                    <li>
                      <a
                        href={config?.socialMedia?.instagram}
                        target="_blank"
                        className="social-icon"
                      >
                        <FaInstagram size={16} />
                      </a>
                    </li>
                  )}
                  {config?.socialMedia?.twitter && (
                    <li>
                      <a
                        href={config?.socialMedia?.twitter}
                        target="_blank"
                        className="social-icon"
                      >
                        <FaTwitter size={16} />
                      </a>
                    </li>
                  )}
                  {config?.socialMedia?.youtube && (
                    <li>
                      <a
                        href={config?.socialMedia?.youtube}
                        target="_blank"
                        className="social-icon"
                      >
                        <FaYoutube size={16} />
                      </a>
                    </li>
                  )}
                  {config?.socialMedia?.linkedin && (
                    <li>
                      <a
                        href={config?.socialMedia?.linkedin}
                        target="_blank"
                        className="social-icon"
                      >
                        <FaLinkedin size={16} />
                      </a>
                    </li>
                  )}
                </div>
              </Col>

              {/* Right Side: Form */}
              <Col lg={7} className="form-panel p-md-5 p-3 bg-white">
                <Form method="post" onSubmit={handleSubmit}>
                  <Row className="g-4">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label htmlFor="name">
                          Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          className="custom-input only-alphabets radius-8"
                          value={formData.name}
                          onChange={handleChange}
                          required
                        />
                        {errors.name && (
                          <span className="text-danger">{errors.name}</span>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label htmlFor="email">
                          Email Address <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          className="custom-input"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                        {errors.email && (
                          <span className="text-danger">{errors.email}</span>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label htmlFor="username">
                          Phone Number <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          className="custom-input radius-8 only-numeric"
                          type="text"
                          maxLength={10}
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                        />
                        {errors.phone && (
                          <span className="text-danger">{errors.phone}</span>
                        )}
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <Form.Group>
                        <Form.Label htmlFor="mess">
                          Message <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          placeholder="Tell us more about your requirements..."
                          className="custom-input"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                        />
                        {errors.message && (
                          <span className="text-danger">{errors.message}</span>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={12} className="mt-5">
                      <button
                        className="submit-btn w-100"
                        type="submit"
                        onClick={handleSubmit}
                      >
                        Send Message
                      </button>
                    </Col>
                  </Row>
                </Form>
              </Col>
            </Row>
          </div>
        </Container>
        {config?.address?.mapLocation && (
          <div className="wg-map">
            <iframe
              src={config?.address?.mapLocation}
              className="map"
              style={{ border: "none" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        )}
      </div>
    </>
  );
}

export default ContactUs;
